import { getMapTheme, normalizeCanvasStyle } from "../utils/mapThemes.js";
import { getMapFont, normalizeMapFont } from "../utils/mapFonts.js";
import { normalizeHierarchyEmphasis } from "../utils/hierarchyEmphasis.js";
import { normalizeEdgeStrength, normalizeRelationshipWeight, resolveRelationshipPresentation } from "../utils/relationshipStrength.js";
import { normalizeEdgeRoutingMode } from "../utils/customEdgeGeometry.js";

function publicationSnapshot(payload) {
  const snapshot = payload?.snapshot;
  if (payload?.schema_version !== 1 || !snapshot || snapshot.schema_version !== 1 ||
      String(payload.map_id || "") !== String(snapshot.id || "") ||
      !Number.isInteger(payload.published_revision) || !payload.content_hash) {
    throw new Error("Unsupported knowledge-map publication");
  }
  return snapshot;
}

function themeTokens(theme) {
  return {
    background: theme.background, grid: theme.grid, node: theme.node,
    nodeBorder: theme.nodeBorder, text: theme.text, focalNode: theme.focalNode,
    focalBorder: theme.focalBorder, coreNode: theme.coreLevelNode,
    coreBorder: theme.coreLevelBorder, detailNode: theme.detailNode,
    detailBorder: theme.detailBorder, labelHalo: theme.labelHalo,
    nodeStyle: theme.nodeStyle, edgeStyle: theme.edgeStyle,
    relationshipLabelStyle: theme.relationshipLabelStyle,
    typography: theme.typography, gridStyle: theme.gridStyle,
  };
}

/** Adapt the canonical R2 envelope to the established renderer contract. */
export function publicationEnvelopeToEmbed(payload) {
  const snapshot = publicationSnapshot(payload);
  const settings = snapshot.view_settings || {};
  const themeId = snapshot.color_mode || "default";
  const canvasStyle = normalizeCanvasStyle(themeId, settings.canvas_style || "theme");
  const relationshipWeight = normalizeRelationshipWeight(settings.relationship_weight);
  const theme = getMapTheme(themeId, canvasStyle);
  const fontId = normalizeMapFont(settings.font_family);
  const font = getMapFont(fontId);
  return {
    schema: "knowledge-map-embed", schema_version: 1,
    map: {
      graph_type: snapshot.graph_type || "concept", view_settings: snapshot.view_settings || {},
      id: String(snapshot.id), title: snapshot.label || "Untitled Map",
      watermark_required: Boolean(payload.watermark_required),
      presentation: {
        theme: themeId, theme_version: 1, theme_tokens: themeTokens(theme),
        layout: snapshot.layout_type || "smart", background: theme.background,
        focus_mode: settings.focus_mode || "disabled",
        declutter_strength: settings.declutter_strength ?? 65,
        show_relationship_labels: settings.show_relationship_labels !== false,
        show_grid: settings.show_grid !== false, grid_mode: settings.grid_mode || "theme",
        canvas_style: canvasStyle, relationship_weight: relationshipWeight,
        hierarchy_emphasis: normalizeHierarchyEmphasis(settings.hierarchy_emphasis),
        font_family: fontId,
        font: font ? { id: fontId, family: font.family, weight: String(font.fontWeight || "100 900"), file: `${fontId}-latin-wght-normal.woff2` } : null,
      },
      nodes: Array.isArray(snapshot.nodes) ? snapshot.nodes : [],
      edges: (Array.isArray(snapshot.edges) ? snapshot.edges : []).map((edge) => {
        const strength = normalizeEdgeStrength(edge.style?.strength);
        const resolved = resolveRelationshipPresentation(strength, relationshipWeight);
        return { ...edge, style: { ...edge.style, width: resolved.width, dash: resolved.dasharray,
          strength, animated: Boolean(edge.style?.animated), routing: normalizeEdgeRoutingMode(edge.style?.routing),
          curve: "offset-v1", arrow: "closed" } };
      }),
    },
  };
}

/** Adapt the canonical R2 envelope to the original full public-view contract. */
export function publicationEnvelopeToPublicMap(payload) {
  const snapshot = publicationSnapshot(payload);
  return {
    graph_type: snapshot.graph_type || "concept",
    id: String(snapshot.id),
    label: snapshot.label || "Untitled Map",
    description: snapshot.description || "",
    creator_attribution: snapshot.public_attribution_enabled
      ? (snapshot.public_attribution_name || "")
      : "",
    metadata: { language: snapshot.output_language || "en" },
    layout_type: snapshot.layout_type || "smart",
    color_mode: snapshot.color_mode || "default",
    view_settings: snapshot.view_settings || {},
    nodes: Array.isArray(snapshot.nodes) ? snapshot.nodes : [],
    edges: Array.isArray(snapshot.edges) ? snapshot.edges : [],
    watermark_required: Boolean(payload.watermark_required),
    published_revision: payload.published_revision,
    revision: payload.published_revision,
    content_hash: payload.content_hash,
  };
}
